import { defineGlobal } from "@faire/globals";
import type { ProductArea } from "@faire/types-observability-api/faire/logging/ProductArea";
import { isPromise } from "@faire/typescript";
import {
  Span,
  SpanContext,
  SpanStatusCode,
  trace,
  SpanOptions,
  Tracer as OTelTracer,
  Attributes,
  Context,
  propagation,
  context,
} from "@opentelemetry/api";

import { Attribution, getAttribution } from "./__internal__/attribution";
import { LogMetadataKey } from "./LogMetadataKey";
import { UnknownError } from "./UnknownError";

type TraceCallback<T> = (span: Span) => T;

export class Tracer {
  private attribution: Attribution;

  private static get otel(): OTelTracer {
    let tracer = getOTelTracer();
    if (!tracer) {
      tracer = trace.getTracer("faire", "0.0.1");
      setOTelTracer(tracer);
    }
    return tracer;
  }

  private constructor(productArea: keyof typeof ProductArea) {
    this.attribution = getAttribution(productArea);
  }

  static getTracer(productArea: keyof typeof ProductArea) {
    const tracer = new Tracer(productArea);
    return { tracer };
  }

  trace<T>(name: string, fn: TraceCallback<T>, options: SpanOptions = {}): T {
    return Tracer.otel.startActiveSpan(
      name,
      this.withAttribution(options),
      (span) => {
        try {
          const result = fn(span);
          if (isPromise(result)) {
            return result
              .catch((error) => {
                Tracer.setError(error, span);
                throw error;
              })
              .finally(() => {
                span.end();
              }) as T;
          }
          span.end();
          return result;
        } catch (error) {
          Tracer.setError(error, span);
          span.end();
          throw error;
        }
      }
    );
  }

  /**
   * Starts a span without making it active, for work that ends in a later
   * event or callback. The caller must end it. Its parent is `parent`, or the
   * active context when omitted.
   */
  startSpan = (
    name: string,
    options: SpanOptions = {},
    parent?: Context
  ): Span => Tracer.otel.startSpan(name, this.withAttribution(options), parent);

  private withAttribution = (options: SpanOptions): SpanOptions => ({
    ...options,
    attributes: {
      [LogMetadataKey.FAIRE_PRODUCT_AREA]: this.attribution.productArea,
      [LogMetadataKey.FAIRE_POD]: this.attribution.pod,
      [LogMetadataKey.FAIRE_PILLAR]: this.attribution.pillar,
      [LogMetadataKey.FAIRE_ONCALL]: this.attribution.oncall,
      ...options.attributes,
    },
  });

  /**
   * Set a filter that should return false if the error should be ignored.
   * Useful if you expect things to be thrown that aren't real errors.
   */
  static setErrorFilter(filter: (error: unknown) => boolean): void {
    setErrorFilter(filter);
  }

  static setError(error: unknown, span?: Span) {
    span ??= trace.getActiveSpan();
    if (!span) {
      return;
    }
    const filter = getErrorFilter();
    if (filter && !filter(error)) {
      return;
    }
    const typedError = new UnknownError(error);
    span.recordException(typedError);
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: typedError.message,
    });
  }

  static getActiveSpanContext(): SpanContext | undefined {
    return trace.getActiveSpan()?.spanContext();
  }

  static setAttributes(attributes: Attributes) {
    const span = trace.getActiveSpan();
    if (!span) {
      return;
    }
    span.setAttributes(attributes);
  }

  static getPropagatedTraceContext(): Record<string, string> {
    const carrier: Record<string, string> = {};
    propagation.inject(context.active(), carrier);
    return carrier;
  }
}

const { get: getOTelTracer, set: setOTelTracer } =
  defineGlobal<OTelTracer>("tracing.tracer");
const { get: getErrorFilter, set: setErrorFilter } = defineGlobal<
  (error: unknown) => boolean
>("tracing.errorFilter");
